<script setup lang="ts">
import type { BrandAppTabId } from '../lib/brandAppContract'

type Part =
  | { d: string; open?: boolean; halo?: boolean; bare?: boolean }
  | { cut: string }
  | { dot: [number, number, number] }

type Shape = { tag: 'path' | 'circle'; attrs: Record<string, string | number> }

const props = defineProps<{ screen: BrandAppTabId; active?: boolean }>()

const STONE = {
  top: '#f8f6f1',
  side: '#e2ddd3',
  edge: '#cbc3b5',
  screen: '#ffffff',
  line: '#ebe6dc',
  shadow: 'rgba(70, 45, 20, 0.16)'
}
const SYMBOL = { face: '#3a1257', side: '#1f0031' }
const MUTED = { face: '#cdc6bc', side: '#b2aa9e' }
const KNOCKOUT = '#ffffff'

const PARTS: Record<BrandAppTabId, Part[]> = {
  home: [
    { d: 'M21 12c0-4.969-4.031-9-9-9S3 7.031 3 12s4.031 9 9 9 9-4.031 9-9z', open: true },
    {
      d: 'M16.438 7.075l-5.494 2.198a3 3 0 0 0-1.672 1.672l-2.198 5.494a0.375 0.375 0 0 0 0.488 0.488l5.494-2.198a3 3 0 0 0 1.672-1.672l2.198-5.494a0.375 0.375 0 0 0-0.488-0.488zM12 13.125a1.125 1.125 0 1 1 1.125-1.125 1.125 1.125 0 0 1-1.125 1.125z',
      bare: true
    }
  ],
  order: [
    { d: 'M16 9.5C16 5.63401 14.2091 2 12 2C9.79086 2 8 5.63401 8 9.5', open: true },
    {
      d: 'M8 7H16C17.8856 7 18.8284 7 19.4142 7.58579C20 8.17157 20 9.11438 20 11V15C20 18.2998 20 19.9497 18.9749 20.9749C17.9497 22 16.2998 22 13 22H11C7.70017 22 6.05025 22 5.02513 20.9749C4 19.9497 4 18.2998 4 15V11C4 9.11438 4 8.17157 4.58579 7.58579C5.17157 7 6.11438 7 8 7Z'
    }
  ],
  loyalty: [
    {
      d: 'M6 3.78571C6 2.79949 6.79949 2 7.78571 2H8.14286C10.2731 2 12 3.7269 12 5.85714V7H9.21429C7.43908 7 6 5.56091 6 3.78571Z'
    },
    {
      d: 'M18 3.78571C18 2.79949 17.2005 2 16.2143 2H15.8571C13.7269 2 12 3.7269 12 5.85714V7H14.7857C16.5609 7 18 5.56091 18 3.78571Z'
    },
    {
      d: 'M4 11V15C4 18.2998 4 19.9497 5.02513 20.9749C6.05025 22 7.70017 22 11 22H13C16.2998 22 17.9497 22 18.9749 20.9749C20 19.9497 20 18.2998 20 15V11Z'
    },
    {
      d: 'M3 9C3 8.25231 3 7.87846 3.20096 7.6C3.33261 7.41758 3.52197 7.26609 3.75 7.16077C4.09808 7 4.56538 7 5.5 7H18.5C19.4346 7 19.9019 7 20.25 7.16077C20.478 7.26609 20.6674 7.41758 20.799 7.6C21 7.87846 21 8.25231 21 9C21 9.74769 21 10.1215 20.799 10.4C20.6674 10.5824 20.478 10.7339 20.25 10.8392C19.9019 11 19.4346 11 18.5 11H5.5C4.56538 11 4.09808 11 3.75 10.8392C3.52197 10.7339 3.33261 10.5824 3.20096 10.4C3 10.1215 3 9.74769 3 9Z',
      halo: true
    },
    { cut: 'M12 11.8V22' }
  ],
  stores: [
    { d: 'M3 10.5V15C3 17.8284 3 19.2426 3.87868 20.1213C4.75736 21 6.17157 21 9 21H14V10.5Z' },
    {
      d: 'M17.7952 2.00254L6.14934 2.03002C4.41118 1.94542 3.96551 3.2116 3.96551 3.83056C3.96551 4.38414 3.89006 5.19117 2.82475 6.70798C1.75944 8.22478 1.83949 8.67537 2.44022 9.72544C2.93879 10.5969 4.20692 10.9374 4.86813 10.9946C6.96834 11.0398 7.99017 9.32381 7.99017 8.1178C9.03203 11.1481 11.9951 11.1481 13.3153 10.8016C14.6381 10.4545 15.7712 9.2118 16.0386 8.1178C16.1945 9.47735 16.6677 10.2707 18.0658 10.8158C19.514 11.3805 20.7594 10.5174 21.3843 9.9642C22.0092 9.41096 22.4102 8.18278 21.2963 6.83288C20.5281 5.90195 20.2079 5.02494 20.1028 4.11599C20.0418 3.58931 19.9883 3.02336 19.5966 2.66323C19.0243 2.13691 18.2031 1.97722 17.7952 2.00254Z',
      halo: true
    },
    { cut: 'M7 17H11' },
    {
      d: 'M18.4985 13.5C20.4315 13.5 21.9985 15.0376 21.9985 16.9343C21.9985 19.0798 19.8828 20.1346 18.8735 21.7429C18.6584 22.0857 18.3541 22.0857 18.1235 21.7429C17.0827 20.1957 14.9985 19.0351 14.9985 16.9343C14.9985 15.0376 16.5655 13.5 18.4985 13.5Z',
      halo: true
    },
    { dot: [18.5, 17, 1.25] }
  ]
}

const SLAB_THICKNESS = [3, 2.5, 2, 1.5, 1, 0.5]
const SYMBOL_X = 24
const SYMBOL_Y = 12
const TRANCHE = 4 / 3

const blurId = `screen-illustration-blur-${useId()}`

function iso(offsetY = 0) {
  return `matrix(.866 .5 -.866 .5 36.3 ${29 + offsetY})`
}

function silhouette(color: string): Shape[] {
  return PARTS[props.screen].flatMap((part): Shape[] =>
    'd' in part
      ? [{ tag: 'path', attrs: { d: part.d, fill: part.open ? 'none' : color, stroke: part.bare ? 'none' : color } }]
      : []
  )
}

function faces(color: string): Shape[] {
  return PARTS[props.screen].flatMap((part): Shape[] => {
    if ('cut' in part) return [{ tag: 'path', attrs: { d: part.cut, stroke: KNOCKOUT } }]
    if ('dot' in part) {
      const [cx, cy, r] = part.dot
      return [{ tag: 'circle', attrs: { cx, cy, r, fill: KNOCKOUT, stroke: 'none' } }]
    }
    const face: Shape = {
      tag: 'path',
      attrs: { d: part.d, fill: part.open ? 'none' : color, stroke: part.bare ? 'none' : color }
    }
    if (!part.halo) return [face]
    return [{ tag: 'path', attrs: { d: part.d, fill: KNOCKOUT, stroke: KNOCKOUT, 'stroke-width': 3.5 } }, face]
  })
}

function layers(palette: { face: string; side: string }) {
  return [
    { key: 'tranche', transform: `translate(0 ${TRANCHE})`, shapes: silhouette(palette.side) },
    { key: 'face', transform: undefined, shapes: faces(palette.face) }
  ]
}

const colored = computed(() => layers(SYMBOL))
const muted = computed(() => layers(MUTED))
</script>

<template>
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <defs>
      <filter :id="blurId" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="1.3" />
      </filter>
    </defs>

    <rect :transform="iso(6)" width="18" height="28" rx="5" :fill="STONE.shadow" :filter="`url(#${blurId})`" />
    <rect
      v-for="offset in SLAB_THICKNESS"
      :key="offset"
      :transform="iso(offset)"
      width="18"
      height="28"
      rx="5"
      :fill="offset > 2 ? STONE.edge : STONE.side"
    />
    <rect :transform="iso()" width="18" height="28" rx="5" :fill="STONE.top" />

    <g :transform="iso()">
      <rect x="2" y="2.5" width="14" height="23" rx="3.5" :fill="STONE.screen" />
      <rect x="4" y="18" width="10" height="2" rx="1" :fill="STONE.line" />
      <rect x="4" y="21.5" width="6" height="2" rx="1" :fill="STONE.line" />
      <path d="M7 1.3h4" :stroke="STONE.side" stroke-width="1" stroke-linecap="round" />
      <circle
        cx="9"
        cy="10"
        r="5"
        :fill="STONE.shadow"
        :class="[
          'origin-center [transform-box:fill-box] motion-safe:transition-[scale,opacity] motion-safe:duration-200 motion-safe:ease-editor',
          active ? 'scale-120 opacity-55' : 'scale-75'
        ]"
      />
    </g>

    <g
      :class="[
        'motion-safe:transition-[translate] motion-safe:duration-200 motion-safe:ease-editor',
        active ? '-translate-y-[5.333px]' : 'group-hover:-translate-y-[2.667px]'
      ]"
    >
      <g
        :transform="`translate(${SYMBOL_X} ${SYMBOL_Y})`"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <g
          v-for="variant in [
            { key: 'muted', layers: muted, visible: !active },
            { key: 'colored', layers: colored, visible: active }
          ]"
          :key="variant.key"
          :class="[
            'motion-safe:transition-opacity motion-safe:duration-200 motion-safe:ease-editor',
            variant.visible ? 'opacity-100' : 'opacity-0'
          ]"
        >
          <g v-for="layer in variant.layers" :key="layer.key" :transform="layer.transform">
            <component :is="item.tag" v-for="(item, index) in layer.shapes" :key="index" v-bind="item.attrs" />
          </g>
        </g>
      </g>
    </g>
  </svg>
</template>
